// Shared pieces of the native forms (observation, accident, news): the page shell, a form-state hook and the
// children list. Questions, options and required flags come from shared/nativeForms.ts (one source with the
// Cloud Functions' validation).

import React, { useContext, useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, Container, Link, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link as RouterLink } from "react-router-dom";
import { AppContext } from "../../Store";
import { submitErrorMessage, type FieldErrors } from "../../shared/nativeForms";

export const REQUIRED = "This is a required question";

export type Role = "teacher" | "manager";

/** Who may open the form. `npm run dev` has a mock teacher, so the manager-only forms are open there. */
export const useAllowed = (role: Role): boolean | null => {
  const { userContext } = useContext(AppContext);
  if (!userContext) return null;
  if (import.meta.env.DEV) return true;
  return role === "manager" ? userContext.isAdmin() : userContext.isTeacher();
};

export interface ChildOption { id: string; name: string }

/** Active children (the portal already leaves out those marked ignore), by id like the forms' option lists. */
export const useChildOptions = (): ChildOption[] => {
  const { childrenData } = useContext(AppContext);
  return useMemo(() => (childrenData ?? [])
    .map((child: any) => ({
      id: child.id as string,
      name: [child.registration?.childFirstName, child.registration?.childLastName].filter(Boolean).join(" "),
    }))
    .sort((a: ChildOption, b: ChildOption) => a.id.localeCompare(b.id)), [childrenData]);
};

export const childLabel = (child: ChildOption) => (child.name ? `${child.name} (${child.id})` : child.id);

interface FormState<T> {
  values: T;
  errors: FieldErrors;
  set: <K extends keyof T>(field: K, value: T[K]) => void;
  submit: (event: React.FormEvent) => void;
  reset: () => void;
  submitting: boolean;
  savedId: string | null;
  failure: string | null;
}

/** Values, validation (shown after the first submit attempt, then live) and the submit call. */
export const useNativeForm = <T,>(
  initial: () => T,
  validate: (values: T) => { ok: true; value: unknown } | { ok: false; errors: FieldErrors },
  save: (value: any) => Promise<{ id: string }>,
): FormState<T> => {
  const [values, setValues] = useState<T>(initial);
  const [attempted, setAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const result = validate(values);
  const errors: FieldErrors = attempted && "errors" in result ? result.errors : {};

  return {
    values,
    errors,
    set: (field, value) => setValues((current) => ({ ...current, [field]: value })),
    submit: async (event) => {
      event.preventDefault();
      setAttempted(true);
      setFailure(null);
      if ("errors" in result || submitting) return;
      setSubmitting(true);
      try {
        setSavedId((await save(result.value)).id);
      } catch (error) {
        console.error("Form submit failed", error);
        setFailure(submitErrorMessage(error));
      } finally {
        setSubmitting(false);
      }
    },
    reset: () => { setValues(initial()); setAttempted(false); setSavedId(null); setFailure(null); },
    submitting,
    savedId,
    failure,
  };
};

interface ShellProps {
  title: string;
  intro?: string;
  allowed: boolean | null;
  deniedText: string;
  oldFormUrl: string;
  form: FormState<any>;
  savedText: string;
  addAnotherLabel: string;
  children: React.ReactNode;
}

export const FormShell: React.FC<ShellProps> = ({ title, intro, allowed, deniedText, oldFormUrl, form, savedText, addAnotherLabel, children }) => (
  <Container maxWidth="sm" sx={{ pb: 6 }}>
    <Button component={RouterLink} to="/" startIcon={<ArrowBackIcon />} size="small" sx={{ mb: 1 }}>Back</Button>
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" component="h1" gutterBottom>{title}</Typography>
      {allowed === null && <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress size={28} /></Box>}
      {allowed === false && <Alert severity="info" variant="outlined">{deniedText}</Alert>}
      {allowed && form.savedId && (
        <Stack spacing={2}>
          <Alert severity="success">{savedText}</Alert>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button variant="contained" onClick={form.reset}>{addAnotherLabel}</Button>
            <Button component={RouterLink} to="/">Back to the portal</Button>
          </Stack>
        </Stack>
      )}
      {allowed && !form.savedId && (
        <Box component="form" noValidate onSubmit={form.submit}>
          {intro && <Typography color="text.secondary" sx={{ mb: 2 }}>{intro}</Typography>}
          <Stack spacing={2.5}>
            {children}
            {form.failure && <Alert severity="error">{form.failure}</Alert>}
            <Button type="submit" variant="contained" size="large" disabled={form.submitting}
              startIcon={form.submitting ? <CircularProgress size={18} color="inherit" /> : undefined}>
              {form.submitting ? "Saving..." : "Submit"}
            </Button>
          </Stack>
        </Box>
      )}
    </Paper>
    <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5, textAlign: "center" }}>
      Having trouble? <Link href={oldFormUrl} target="_blank" rel="noopener">Use the old Google Form</Link> instead.
    </Typography>
  </Container>
);

export const SelectField: React.FC<{
  label: string; value: string; options: readonly string[]; onChange: (value: string) => void; error?: string; required?: boolean;
}> = ({ label, value, options, onChange, error, required = true }) => (
  <TextField select fullWidth required={required} label={label} value={value} error={!!error} helperText={error}
    onChange={(event) => onChange(event.target.value)}>
    {options.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
  </TextField>
);

export const TextAreaField: React.FC<{
  label: string; value: string; onChange: (value: string) => void; error?: string; required?: boolean; rows?: number;
}> = ({ label, value, onChange, error, required = true, rows = 3 }) => (
  <TextField fullWidth multiline minRows={rows} required={required} label={label} value={value} error={!!error} helperText={error}
    onChange={(event) => onChange(event.target.value)} />
);

export const DateField: React.FC<{
  label: string; value: string; onChange: (value: string) => void; error?: string;
}> = ({ label, value, onChange, error }) => (
  <TextField fullWidth required type="date" label={label} value={value} error={!!error} helperText={error}
    InputLabelProps={{ shrink: true }} onChange={(event) => onChange(event.target.value)} />
);

/**
 * TODO(photo): the Google Forms collect one photo/file that lands in a Drive folder and is stored as
 * photoFileUrl "https://drive.google.com/open?id=<fileId>". The functions already accept that field, but the upload
 * itself needs an owner decision (docs/native-forms.md, "Photos"), so for now the old form is the way to add one.
 */
export const PhotoNotice: React.FC<{ oldFormUrl: string }> = ({ oldFormUrl }) => (
  <Alert severity="info" variant="outlined" sx={{ alignItems: "center" }}>
    Photo upload is not available in the portal yet. To add a photo, <Link href={oldFormUrl} target="_blank" rel="noopener">use the old Google Form</Link>.
  </Alert>
);
